import { ICreateBranchPayload, ISupplierBody, ITransactionPayload } from "../../types";
import api from "../api";

type ApiResponse = {
  data?: any;
  content?: any;
  items?: any;
};

export const unwrapList = (response: ApiResponse): any[] => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(response?.content)) {
    return response.content;
  }

  if (Array.isArray(response?.items)) {
    return response.items;
  }

  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.data)) {
      return payload.data;
    }
    if (Array.isArray(payload.content)) {
      return payload.content;
    }
    if (Array.isArray(payload.items)) {
      return payload.items;
    }
  }

  return [];
};

export const getBranch = async () => {
  const response = await api.get("/branch");

  return unwrapList(response);
};

export const createBranch = async (payload: ICreateBranchPayload) => {
  const response = await api.post("/branch", payload);

  return response.data;
};

export const createBranches = async (branches: Array<{ name: string }>) => {
  const response = await api.post("/branch/branches", branches);

  return response.data;
};

export const editBranch = async (payload: ICreateBranchPayload) => {
  const response = await api.post(`/branch`, payload);

  return response.data;
};

export const deleteBranch = async (id: number) => {
  const response = await api.delete(`/branch/${id}`);

  return response.data;
};

export const getTransactions = async () => {
  const response = await api.get("/transactions");

  return response.data;
};

export const createTransaction = async (payload: ITransactionPayload, file?: File | null) => {
  const formData = new FormData();

  formData.append("transaction", JSON.stringify(payload));

  if (file) {
    formData.append("file", file);
  }

  const response = await api.post("/transactions", formData);

  return response.data;
};

export const editTransaction = async (id: string, payload: ITransactionPayload, file?: File | null) => {
  if (!file) {
    const response = await api.put(`/transactions/${id}`, payload);
    return response.data;
  }

  const formData = new FormData();

  formData.append("transaction", JSON.stringify(payload));
  formData.append("file", file);

  const response = await api.put(`/transactions/${id}`, formData);

  return response.data;
};

export const getSegments = async () => {
  const response = await api.get("/operations");

  return unwrapList(response);
};

export const createSegments = async (value: string) => {
  const response = await api.post("/operations", { name: value });

  return response.data;
};

export const editSegments = async (payload: ICreateBranchPayload) => {
  const response = await api.post(`/operations`, payload);

  return response.data;
};

export const deleteSegments = async (id: number) => {
  const response = await api.delete(`/operations/${id}`);

  return response.data;
};

export const getCategories = async () => {
  const response = await api.get("/category");

  return unwrapList(response);
};

type CategoryType = { type?: "RECEITA" | "DESPESA" };
type CategoryExpenseType = { expenseType?: "DIRETO" | "OPERACIONAL" };

type CreateCategory = ICreateBranchPayload & CategoryType & CategoryExpenseType;

export const createCategory = async (payload: CreateCategory) => {
  const response = await api.post("/category", payload);

  return response.data;
};

export const editCategory = async (payload: CreateCategory) => {
  const response = await api.post("/category", payload);

  return response.data;
};

export const deleteCategory = async (id: number) => {
  const response = await api.delete(`/category/${id}`);

  return response.data;
};

export const createSubCategory = async (payload: ICreateBranchPayload) => {
  const response = await api.post("/category", payload);

  return response.data;
};

export const getSubCategories = async (id: string) => {
  const response = await api.get(`/subcategory?categoryId=${id}`);

  return response.data;
};

export const getAllSubCategories = async () => {
  const response = await api.get("/subcategory");

  return response.data;
};

export const createSubCategories = async (id: string, payload: ICreateBranchPayload) => {
  const response = await api.post(`/subcategory`, {
    ...payload,
    displayOrder: 0,
    active: true,
    categoryId: id,
  });

  return response.data;
};

export const editSubCategories = async (id: string, payload: ICreateBranchPayload) => {
  const response = await api.post(`/subcategory`, {
    ...payload,
    displayOrder: 0,
    active: true,
    categoryId: id,
  });

  return response.data;
};

export const deleteSubCategories = async (id: number) => {
  const response = await api.delete(`/subcategory/${id}`);

  return response.data;
};

export const getAccounts = async () => {
  const response = await api.get("/rio/accounts");

  return unwrapList(response);
};

export const getSuppliers = async (type?: string) => {
  const url = type ? `/supplier-client?type=${type}` : "/supplier-client";
  const response = await api.get(url);

  return response.data;
};

export const getContracts = async () => {
  const response = await api.get("/contracts");

  return response.data;
};

export const getAllSuppliers = async () => {
  const response = await api.get("/clients?activeOnly=true");

  return response.data;
};

export const createSuppliers = async (payload: ISupplierBody) => {
  const response = await api.post("/supplier-client ", payload);

  return response.data;
};

export const editSuppliers = async (payload: ISupplierBody) => {
  const response = await api.post(`/supplier-client`, payload);

  return response.data;
};

export const deleteSuppliers = async (id: number) => {
  const response = await api.delete(`/supplier-client/${id}`);

  return response.data;
};

export const createAccounts = async (payload: ICreateBranchPayload) => {
  const response = await api.post("/rio/accounts", payload);

  return response.data;
};

export const getAssets = async () => {
  const response = await api.get("/vehicles");

  return response.data;
};

export const getDrivers = async () => {
  const response = await api.get("/drivers");

  return unwrapList(response);
};

export const getClients = async () => {
  const response = await api.get("/clients");

  return unwrapList(response);
};

export const getVehicles = async () => {
  const response = await api.get("/vehicles");

  return unwrapList(response);
};

export const getDRESummary = async (startDate: string, endDate: string) => {
  const response = await api.get(`/dre/summary?startDate=${startDate}&endDate=${endDate}`);

  return response.data;
};

type TableDataQuery = {
  page?: number;
  size?: number;
};

export const getTableData = async (startDate: string, endDate: string, type: string, query?: TableDataQuery) => {
  const params = new URLSearchParams({
    type,
    startDate,
    endDate,
  });

  if (typeof query?.page === "number") {
    params.append("page", query.page.toString());
  }

  if (typeof query?.size === "number") {
    params.append("size", query.size.toString());
  }

  const response = await api.get(`/transactions?${params.toString()}`);

  return response.data;
};

export const deleteTransaction = async (id: string) => {
  const response = await api.delete(`/transactions/${id}`);
  return response.data;
};

export const getDetailTransaction = async (id: string) => {
  const response = await api.get(`/transactions/${id}`);
  return response.data;
};

export const uploadFile = async (id: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post(`/transactions/upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const getFile = async (transactionId: string) => {
  const response = await api.get(`/transactions/${transactionId}/attachments`);
  return response.data;
};

export const deleteFile = async (transactionId: string) => {
  const response = await api.delete(`/transactions/${transactionId}/attachments`);
  return response.data;
};
